export type SpeakerTag = "LAB" | "ACADEMIA" | "INDUSTRY";

export interface Speaker {
  name: string;
  /** Omitted where the affiliation is not confirmed yet. */
  org?: string;
  /** Title, shown as "title, company". Omitted when we do not know it. */
  role?: string;
  tag?: SpeakerTag;
  initials: string;
  photo?: string;
  /** Speakers sharing a pair key share one card, as two portraits side by side. */
  pair?: string;
}

const PEOPLE = "/marketing/convergence/people";

/** Confirmed speakers only. Invited-but-unconfirmed names are held back. */
export const SPEAKERS: readonly Speaker[] = [
  {
    name: "Anastasios Angelopoulos",
    org: "Arena",
    role: "CEO",
    tag: "INDUSTRY",
    initials: "AA",
    photo: `${PEOPLE}/anastasios-angelopoulos-512.jpg`,
  },
  {
    name: "Ben Huffman",
    org: "Contra",
    role: "Co-founder and CEO",
    tag: "INDUSTRY",
    initials: "BH",
    photo: `${PEOPLE}/ben-huffman-512.jpg`,
    pair: "contra",
  },
  {
    name: "Allison Nulty",
    org: "Contra",
    role: "Head of Contra Labs",
    tag: "INDUSTRY",
    initials: "AN",
    photo: `${PEOPLE}/allison-nulty-512.jpg`,
    pair: "contra",
  },
  {
    name: "Sahil Bhaiwala",
    org: "Handshake",
    role: "CSO",
    tag: "INDUSTRY",
    initials: "SB",
    photo: `${PEOPLE}/sahil-bhaiwala-512.jpg`,
  },
  {
    name: "Dylan Porges",
    org: "Deeptune",
    role: "CBO",
    tag: "INDUSTRY",
    initials: "DP",
    photo: `${PEOPLE}/dylan-porges-512.jpg`,
  },
  {
    name: "Logan Kilpatrick",
    org: "Google DeepMind",
    role: "Member of Technical Staff",
    tag: "LAB",
    initials: "LK",
    photo: `${PEOPLE}/logan-kilpatrick-512.jpg`,
  },
  {
    name: "Alex Trott",
    org: "Databricks",
    tag: "INDUSTRY",
    initials: "AT",
    photo: `${PEOPLE}/alex-trott-512.jpg`,
  },
  {
    name: "Ece Kamar",
    org: "Turing",
    role: "CTO",
    tag: "INDUSTRY",
    initials: "EK",
    photo: `${PEOPLE}/ece-kamar-512.jpg`,
  },
  {
    name: "Pratyush Maini",
    org: "DatologyAI",
    role: "Cornell professor / Founding member",
    tag: "INDUSTRY",
    initials: "PM",
    photo: `${PEOPLE}/pratyush-maini-512.jpg`,
  },
  {
    name: "Melissa Pan",
    org: "UC Berkeley",
    role: "PhD student",
    tag: "ACADEMIA",
    initials: "MP",
    photo: `${PEOPLE}/melissa-pan-512.jpg`,
  },
  {
    name: "Andy Zhang",
    org: "UC Berkeley",
    role: "Researcher",
    tag: "ACADEMIA",
    initials: "AZ",
    photo: `${PEOPLE}/andy-zhang-512.jpg`,
  },
  {
    name: "Steven Dillmann",
    org: "Stanford University",
    role: "AI4Science PhD",
    tag: "ACADEMIA",
    initials: "SD",
    photo: `${PEOPLE}/steven-dillmann-512.jpg`,
  },
  {
    name: "Caleb Chuck",
    org: "Princeton University",
    role: "Postdoctoral Researcher",
    tag: "ACADEMIA",
    initials: "CC",
    photo: `${PEOPLE}/caleb-chuck-512.jpg`,
  },
];

/**
 * The lineup as it is dealt onto cards. A speaker gets a card to themselves
 * unless they carry a pair key, in which case the pair shares one.
 */
export const SPEAKER_CARDS: readonly (readonly Speaker[])[] = (() => {
  const cards: Speaker[][] = [];
  const pairs = new Map<string, Speaker[]>();

  for (const speaker of SPEAKERS) {
    const key = speaker.pair;
    if (!key) {
      cards.push([speaker]);
      continue;
    }
    const open = pairs.get(key);
    if (open) {
      open.push(speaker);
      continue;
    }
    const card = [speaker];
    pairs.set(key, card);
    cards.push(card);
  }

  return cards;
})();

/**
 * Company marks in their own colours, keyed by the `org` string above.
 * Speakers whose mark we do not hold yet fall back to an initials tile.
 */
export const ORG_LOGOS: Readonly<Record<string, string>> = {
  Arena: "/marketing/convergence/orgs-color/arena.png",
  Contra: "/marketing/convergence/orgs-color/contra.png",
  Databricks: "/marketing/convergence/orgs-color/databricks.png",
  DatologyAI: "/marketing/convergence/orgs-color/datologyai.png",
  Deeptune: "/marketing/convergence/orgs-color/deeptune.png",
  "Google DeepMind": "/marketing/convergence/orgs-color/google-deepmind.png",
  Handshake: "/marketing/convergence/orgs-color/handshake.png",
  "Princeton University": "/marketing/convergence/orgs-color/princeton.png",
  "Stanford University": "/marketing/convergence/orgs-color/stanford.png",
  Turing: "/marketing/convergence/orgs-color/turing.png",
  "UC Berkeley": "/marketing/convergence/orgs-color/berkeley.png",
  micro1: "/marketing/convergence/orgs-color/micro1.png",
};

export interface SpeakerCompany {
  name: string;
  logo: string;
  url: string;
}

/** The organisations the confirmed speakers come from, for the wall. */
export const SPEAKER_COMPANIES: readonly SpeakerCompany[] = [
  {
    name: "Deeptune",
    logo: ORG_LOGOS.Deeptune as string,
    url: "https://deeptune.com",
  },
  {
    name: "Handshake",
    logo: ORG_LOGOS.Handshake as string,
    url: "https://joinhandshake.com",
  },
  {
    name: "Databricks",
    logo: ORG_LOGOS.Databricks as string,
    url: "https://www.databricks.com",
  },
  {
    name: "DatologyAI",
    logo: ORG_LOGOS.DatologyAI as string,
    url: "https://www.datologyai.com",
  },
  {
    name: "Stanford",
    logo: ORG_LOGOS["Stanford University"] as string,
    url: "https://www.stanford.edu",
  },
  {
    name: "Contra",
    logo: ORG_LOGOS.Contra as string,
    url: "https://contra.com",
  },
  {
    name: "Arena",
    logo: ORG_LOGOS.Arena as string,
    url: "https://lmarena.ai",
  },
  {
    name: "Turing",
    logo: ORG_LOGOS.Turing as string,
    url: "https://www.turing.com",
  },
  {
    name: "Google DeepMind",
    logo: ORG_LOGOS["Google DeepMind"] as string,
    url: "https://deepmind.google",
  },
  {
    name: "Princeton",
    logo: ORG_LOGOS["Princeton University"] as string,
    url: "https://ai.princeton.edu",
  },
  {
    name: "UC Berkeley",
    logo: ORG_LOGOS["UC Berkeley"] as string,
    url: "https://www.berkeley.edu",
  },
];
